import type { NonEmptyArray } from "@/types/utils.ts";

import { uniqBy } from "es-toolkit";

import { isDefined } from "@/types/utils.ts";

/**
 * Immutable wrappers around collections that provide a fluent API for chaining operations.
 */

interface CollectionWrapper<T> {
  value: () => T[];
  first: () => T | undefined;

  map: <U>(fn: (item: T, index: number, array: readonly T[]) => U) => CollectionWrapper<U>;
  filter: ((predicate: (value: T, index: number, array: readonly T[]) => boolean) => CollectionWrapper<T>) &
    (<U extends T>(predicate: (value: T, index: number, array: readonly T[]) => value is U) => CollectionWrapper<U>);
  /**
   * A map + filter(isDefined) in a single pass
   */
  filterMap: <U>(fn: (item: T, index: number, array: readonly T[]) => U | null | undefined) => CollectionWrapper<U>;
  flatMap: <U>(fn: (item: T, index: number, array: readonly T[]) => U[]) => CollectionWrapper<U>;
  groupBy: <K>(keyFn: (item: T) => K) => MapWrapper<K, NonEmptyArray<T>>;
  /**
   * A variant of `groupBy` that assumes each key is unique and maps to a single item.
   * If multiple items map to the same key, an error is thrown.
   */
  groupByUnique: <K>(keyFn: (item: T) => K) => MapWrapper<K, T>;
  slice: (start: number, end?: number) => CollectionWrapper<T>;
  /**
   * Sorts the collection in ascending order.
   *
   * Only available when we're dealing with an array of `number` or `string`.
   */
  sort: () => T extends number | string ? CollectionWrapper<T> : never;
  sortBy: <K extends string | number>(keyFn: (item: T) => K) => CollectionWrapper<T>;
  toSet: () => SetWrapper<T>;
  uniqBy: <K>(keyFn: (item: T) => K) => CollectionWrapper<T>;
}

interface SetWrapper<T> {
  value: () => Set<T>;
  // Current - other
  difference: (other: Iterable<T>) => SetWrapper<T>;
  // Current ∩ other
  intersect: (other: Iterable<T>) => SetWrapper<T>;
  toggle: (item: T) => SetWrapper<T>;
  // Current ∪ other
  union: (other: Iterable<T>) => SetWrapper<T>;
  toArray: () => CollectionWrapper<T>;
}

interface MapWrapper<K, V> {
  value: () => Map<K, V>;
  keys: () => CollectionWrapper<K>;
  values: () => CollectionWrapper<V>;
  entries: () => CollectionWrapper<[K, V]>;

  toObject: () => K extends string | number ? Record<K, V> : { [key: string]: V };

  mapValues: <U>(fn: (value: V, key: K, map: ReadonlyMap<K, V>) => U) => MapWrapper<K, U>;
  filterMap: <U>(fn: (value: V, key: K, map: ReadonlyMap<K, V>) => U | null | undefined) => MapWrapper<K, U>;
}

/**
 * A wrapper to enable chaining of common array operations.
 *
 * Do : add additional methods when they are **commonly used** and often chained with other collection methods.
 * Do not : add every array util you can think of : the goal is not to force everyone to use this wrapper.
 */
export function chain<T>(array: T[]): CollectionWrapper<T> {
  function filter(predicate: (value: T, index: number, array: readonly T[]) => boolean): CollectionWrapper<T>;
  function filter<U extends T>(
    predicate: (value: T, index: number, array: readonly T[]) => value is U,
  ): CollectionWrapper<U>;
  function filter(predicate: (value: T, index: number, array: readonly T[]) => boolean) {
    return chain(array.filter(predicate));
  }

  return {
    value() {
      return array;
    },
    first() {
      return array[0];
    },
    map(fn) {
      return chain(array.map(fn));
    },
    filter,
    filterMap<U>(predicate: (item: T, index: number, array: T[]) => U | null | undefined) {
      const result: U[] = [];

      for (const [index, item] of array.entries()) {
        const mapped = predicate(item, index, array);
        if (isDefined(mapped)) {
          result.push(mapped);
        }
      }

      return chain(result);
    },
    flatMap(fn) {
      return chain(array.flatMap(fn));
    },
    groupBy<K>(keyFn: (item: T) => K) {
      const map = new Map<K, NonEmptyArray<T>>();

      for (const item of array) {
        const key = keyFn(item);
        const group: Array<T> = map.get(key) ?? [];
        group.push(item);
        map.set(key, group as NonEmptyArray<T>);
      }

      return chainMap(map);
    },
    groupByUnique<K>(keyFn: (item: T) => K) {
      const map = new Map<K, T>();

      for (const item of array) {
        const key = keyFn(item);
        if (map.has(key)) {
          throw new Error(`Duplicate key found in groupByUnique for key "${String(key)}"`);
        }
        map.set(key, item);
      }

      return chainMap(map);
    },
    slice(start, end) {
      return chain(array.slice(start, end));
    },
    sort() {
      return chain(
        array.slice().sort((a, b) => {
          if (a < b) return -1;
          if (a > b) return 1;
          return 0;
        }),
      ) as ReturnType<CollectionWrapper<T>["sort"]>;
    },
    sortBy(keyFn) {
      const sorted = array.slice().sort((a, b) => {
        const valueA = keyFn(a);
        const valueB = keyFn(b);
        if (valueA < valueB) return -1;
        if (valueA > valueB) return 1;

        return 0;
      });

      return chain(sorted);
    },
    toSet() {
      return chainSet(new Set(array));
    },
    uniqBy(keyFn) {
      return chain(uniqBy(array, keyFn));
    },
  };
}

export function chainMap<K, V>(map: Map<K, V>): MapWrapper<K, V> {
  return {
    value() {
      return map;
    },
    keys() {
      return chain(Array.from(map.keys()));
    },
    values() {
      return chain(Array.from(map.values()));
    },
    entries() {
      return chain(Array.from(map.entries()));
    },

    toObject() {
      const obj: any = {};

      for (const [key, value] of map.entries()) {
        obj[key as any] = value;
      }

      return obj;
    },

    mapValues<U>(fn: (value: V, key: K, map: ReadonlyMap<K, V>) => U) {
      const result = new Map<K, U>();

      for (const [key, value] of map.entries()) {
        result.set(key, fn(value, key, map));
      }

      return chainMap(result);
    },

    filterMap<U>(fn: (value: V, key: K, map: ReadonlyMap<K, V>) => U | null | undefined) {
      const result = new Map<K, U>();

      for (const [key, value] of map.entries()) {
        const mapped = fn(value, key, map);
        if (isDefined(mapped)) {
          result.set(key, mapped);
        }
      }

      return chainMap(result);
    },
  };
}

export function chainSet<T>(set: Set<T>): SetWrapper<T> {
  return {
    value() {
      return set;
    },
    difference(other) {
      const otherSet = new Set(other);
      const result = new Set<T>();

      for (const item of set) {
        if (!otherSet.has(item)) {
          result.add(item);
        }
      }

      return chainSet(result);
    },
    union(other) {
      return chainSet(new Set([...set, ...other]));
    },
    toArray() {
      return chain(Array.from(set));
    },
    intersect: function (other: Iterable<T>): SetWrapper<T> {
      const result = new Set<T>();

      for (const item of other) {
        if (set.has(item)) {
          result.add(item);
        }
      }

      return chainSet(result);
    },
    toggle: function (item: T): SetWrapper<T> {
      const result = new Set(set);

      if (result.has(item)) {
        result.delete(item);
      } else {
        result.add(item);
      }

      return chainSet(result);
    },
  };
}
